"use client";

import { useState, useEffect, useRef } from "react";
import { Check, ArrowLeft } from "lucide-react";

type Stage = "request" | "verify" | "reset" | "success";

interface FormData {
  email: string;
  otp: string[];
  newpassword: string;
  confirmpassword: string;
}

const API_BASE = process.env.NEXT_PUBLIC_API_URL || "";

export default function ForgotPasswordPage() {
  const [stage, setStage] = useState<Stage>("request");
  const [formData, setFormData] = useState<FormData>({
    email: "",
    otp: ["", "", "", "", "", ""],
    newpassword: "",
    confirmpassword: "",
  });

  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [apiError, setApiError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [resetToken, setResetToken] = useState("");
  const [userId, setUserId] = useState<number | null>(null);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ripplesRef = useRef<{ x: number; y: number; t: number }[]>([]);
  const animRef = useRef<number | null>(null);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const DURATION = 1800;
    const MAX_R = 160;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    const draw = (now: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ripplesRef.current = ripplesRef.current.filter((r) => now - r.t < DURATION);
      for (const r of ripplesRef.current) {
        const p = (now - r.t) / DURATION;
        const eased = 1 - Math.pow(1 - p, 3);
        ctx.beginPath();
        ctx.arc(r.x, r.y, eased * MAX_R, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(199,171,95,${(1 - p) * 0.6})`;
        ctx.lineWidth = 1.5 + (1 - p) * 0.4;
        ctx.stroke();
        if (p > 0.15) {
          const p2 = (p - 0.15) / 0.85;
          ctx.beginPath();
          ctx.arc(r.x, r.y, p2 * MAX_R * 0.55, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(199,171,95,${(1 - p2) * 0.28})`;
          ctx.lineWidth = 1 + (1 - p2) * 0.25;
          ctx.stroke();
        }
      }
      animRef.current = requestAnimationFrame(draw);
    };
    animRef.current = requestAnimationFrame(draw);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); ro.disconnect(); };
  }, []);

  useEffect(() => {
    if (stage === "verify") startResendTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [stage]);

  const startResendTimer = () => {
    setResendTimer(60);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setResendTimer((t) => {
        if (t <= 1) { if (timerRef.current) clearInterval(timerRef.current); return 0; }
        return t - 1;
      });
    }, 1000);
  };

  const spawnRipple = (cx: number, cy: number) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    ripplesRef.current.push({ x: cx - rect.left, y: cy - rect.top, t: performance.now() });
  };

  const handleOtpChange = (idx: number, val: string) => {
    const digit = val.replace(/\D/g, "").slice(-1);
    const next = [...formData.otp];
    next[idx] = digit;
    setFormData((p) => ({ ...p, otp: next }));
    setErrors((p) => ({ ...p, otp: "" }));
    setApiError("");
    if (digit && idx < 5) otpRefs.current[idx + 1]?.focus();
  };

  const handleOtpKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !formData.otp[idx] && idx > 0) otpRefs.current[idx - 1]?.focus();
  };

  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (!pasted) return;
    const next = ["", "", "", "", "", ""];
    pasted.split("").forEach((d, i) => { next[i] = d; });
    setFormData((p) => ({ ...p, otp: next }));
    setErrors((p) => ({ ...p, otp: "" }));
    otpRefs.current[Math.min(pasted.length, 5)]?.focus();
  };

  // ─── STEP 1: just send email ───────────────────────────────────────────────
  const handleRequestSubmit = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email) { setErrors({ email: "Email is required" }); return; }
    if (!/\S+@\S+\.\S+/.test(email)) { setErrors({ email: "Enter a valid email" }); return; }

    setErrors({});
    setApiError("");
    setSuccessMsg("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),   // ✅ only email
      });
      const data = await response.json();
      console.log("forgot-password response:", JSON.stringify(data)); // debug

      if (!response.ok || !data.success) {
        setApiError(data.message || "Failed to send verification code");
        return;
      }

      // forgot-password returns: { reset_token, user_id }
      const tok = data.reset_token || data.resetToken || data.token || "";
      const uid = data.user_id ?? data.userId ?? data.userid ?? data.id ?? null;
      if (tok) setResetToken(tok);
      if (uid !== null) setUserId(uid);

      setSuccessMsg(data.message || "Verification code sent successfully");
      setStage("verify");
      startResendTimer();
    } catch (error) {
      console.error("Forgot password error:", error);
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── STEP 2: verify OTP — save token + id returned here ───────────────────
  const handleVerifySubmit = async () => {
    const code = formData.otp.join("");
    const email = formData.email.trim().toLowerCase();
    if (code.length !== 6) { setErrors({ otp: "Enter all 6 digits" }); return; }

    setErrors((p) => ({ ...p, otp: "" }));
    setApiError("");
    setSuccessMsg("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/verify-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, otp: code }),
      });
      const data = await response.json();
      console.log("verify-reset-otp response:", JSON.stringify(data)); // debug

      if (!response.ok || !data.success) {
        setApiError(data.message || "OTP verification failed");
        return;
      }

      // Only update token/userId if verify response actually returns them
      // (verify returns nothing, so we keep what was saved from forgot-password)
      const tok = data.resetToken || data.token || data.reset_token || data.accessToken || "";
      const uid = data.userId ?? data.userid ?? data.user_id ?? data.id ?? data.uid ?? null;
      if (tok) setResetToken(tok);
      if (uid !== null) setUserId(uid);

      setSuccessMsg(data.message || "OTP verified successfully");
      setStage("reset");
    } catch (error) {
      console.error("Verify OTP error:", error);
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── RESEND OTP ────────────────────────────────────────────────────────────
  const handleResendOtp = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email) { setApiError("Email is required"); return; }

    setApiError("");
    setSuccessMsg("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/resend-reset-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),   // ✅ only email
      });
      const data = await response.json();
      console.log("resend-reset-otp response:", JSON.stringify(data)); // debug

      if (!response.ok || !data.success) {
        setApiError(data.message || "Failed to resend OTP");
        return;
      }

      // Refresh token/userId if resend returns them
      const tok = data.resetToken || data.token || data.reset_token || "";
      const uid = data.userId ?? data.userid ?? data.user_id ?? data.id ?? null;
      if (tok) setResetToken(tok);
      if (uid !== null) setUserId(uid);

      setFormData((p) => ({ ...p, otp: ["", "", "", "", "", ""] }));
      setSuccessMsg(data.message || "OTP resent successfully");
      startResendTimer();
      otpRefs.current[0]?.focus();
    } catch (error) {
      console.error("Resend OTP error:", error);
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ─── STEP 3: reset password — always send id + token + email + password ───
  const handleResetSubmit = async () => {
    const e: Record<string, string> = {};
    const email = formData.email.trim().toLowerCase();

    if (!formData.newpassword) e.newpassword = "Password is required";
    else if (formData.newpassword.length < 8) e.newpassword = "Minimum 8 characters";
    if (!formData.confirmpassword) e.confirmpassword = "Please confirm your password";
    else if (formData.newpassword !== formData.confirmpassword) e.confirmpassword = "Passwords do not match";
    if (Object.keys(e).length) { setErrors(e); return; }

    setErrors({});
    setApiError("");
    setSuccessMsg("");
    setIsLoading(true);

    try {
      const response = await fetch(`${API_BASE}/api/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          id: userId,
          token: resetToken,
          email,
          password: formData.newpassword,
        }),
      });
      const data = await response.json();
      console.log("reset-password response:", JSON.stringify(data)); // debug

      if (!response.ok || !data.success) {
        setApiError(data.message || "Password reset failed");
        return;
      }

      setSuccessMsg(data.message || "Password reset successfully");
      setStage("success");
    } catch (error) {
      console.error("Reset password error:", error);
      setApiError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const pwStrength = (() => {
    const p = formData.newpassword;
    if (!p) return 0;
    let s = 0;
    if (p.length >= 8) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    return s;
  })();

  const strengthLabel = ["", "Weak", "Fair", "Good", "Strong"][pwStrength];
  const strengthColor = ["", "#e53e3e", "#dd6b20", "#C7AB5F", "#38a169"][pwStrength];
  const stageIndex = { request: 0, verify: 1, reset: 2, success: 3 }[stage];

  return (
    <>
      <style jsx>{`
        @import url("https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap");
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        header, footer, nav { display: none !important; }
        :root { --navy: #051173; --gold: #c7ab5f; --white: #ffffff; --black: #000000; }
        .fp { font-family: "Cormorant Garamond", Georgia, serif; position: fixed; inset: 0; width: 100vw; height: 100vh; height: 100dvh; overflow: hidden; z-index: 9999; cursor: crosshair; }
        .bg-video { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; object-position: center; z-index: 0; }
        .bg-scrim { position: absolute; inset: 0; background: linear-gradient(135deg, #000000 0%, rgba(0,0,0,0.18) 50%, rgba(0,0,0,0.52) 100%); z-index: 1; }
        .bg-tex { position: absolute; inset: 0; background: repeating-linear-gradient(-55deg, transparent, transparent 90px, rgba(199,171,95,0.025) 90px, rgba(199,171,95,0.025) 91px); z-index: 2; }
        .bg-canvas { position: absolute; inset: 0; width: 100%; height: 100%; pointer-events: none; z-index: 3; }
        .bg-compass { position: absolute; top: 2.5rem; left: 2.5rem; z-index: 4; opacity: 0.22; pointer-events: none; }
        .c-tl, .c-br, .c-tr, .c-bl { position: absolute; width: 52px; height: 52px; z-index: 4; pointer-events: none; }
        .c-tl { top: 20px; left: 20px; border-top: 1.5px solid rgba(199,171,95,0.55); border-left: 1.5px solid rgba(199,171,95,0.55); }
        .c-tr { top: 20px; right: 20px; border-top: 1.5px solid rgba(199,171,95,0.55); border-right: 1.5px solid rgba(199,171,95,0.55); }
        .c-bl { bottom: 20px; left: 20px; border-bottom: 1.5px solid rgba(199,171,95,0.55); border-left: 1.5px solid rgba(199,171,95,0.55); }
        .c-br { bottom: 20px; right: 20px; border-bottom: 1.5px solid rgba(199,171,95,0.55); border-right: 1.5px solid rgba(199,171,95,0.55); }
        .bg-stats { position: absolute; bottom: 3rem; right: 3rem; z-index: 4; pointer-events: none; display: flex; flex-direction: row; gap: 0.75rem; }
        .bg-stat { border: 1px solid rgba(199,171,95,0.2); background: rgba(0,0,0,0.25); backdrop-filter: blur(6px); border-radius: 6px; padding: 0.45rem 0.8rem; display: flex; flex-direction: column; align-items: flex-start; }
        .bg-stat-n { font-family: "Cormorant Garamond", serif; font-size: 1.2rem; font-style: italic; color: var(--gold); line-height: 1; }
        .bg-stat-l { font-family: "DM Mono", monospace; font-size: 8px; letter-spacing: 0.2em; text-transform: uppercase; color: rgba(255,255,255,0.3); margin-top: 2px; }
        .bg-badge { position: absolute; top: 2.4rem; right: 2.5rem; z-index: 4; pointer-events: none; display: inline-flex; align-items: center; gap: 0.5rem; border: 1px solid rgba(199,171,95,0.32); background: rgba(0,0,0,0.28); backdrop-filter: blur(8px); border-radius: 100px; padding: 0.3rem 0.8rem; font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.28em; text-transform: uppercase; color: var(--gold); }
        .bg-badge::before { content: ""; width: 5px; height: 5px; border-radius: 50%; background: var(--gold); flex-shrink: 0; }
        .form-wrap { position: absolute; inset: 0; z-index: 5; display: flex; align-items: center; justify-content: center; padding: 2rem 1.5rem; overflow-y: auto; -webkit-overflow-scrolling: touch; pointer-events: none; }
        .fc { width: 100%; max-width: 420px; background: rgba(255,255,255,0.96); backdrop-filter: blur(20px); -webkit-backdrop-filter: blur(20px); border-radius: 16px; padding: 2.75rem 2.5rem 2.25rem; box-shadow: 0 24px 64px rgba(0,0,0,0.28), 0 0 0 1px rgba(199,171,95,0.15); pointer-events: all; opacity: 0; transform: translateY(28px) scale(0.98); transition: opacity 0.7s ease, transform 0.7s ease; }
        .fc.on { opacity: 1; transform: translateY(0) scale(1); }
        .fc::before { content: ""; display: block; width: 2.5rem; height: 2px; background: var(--gold); margin-bottom: 1.5rem; }
        .steps { display: flex; align-items: center; margin-bottom: 1.75rem; }
        .step-item { display: flex; flex-direction: column; align-items: center; }
        .step-dot { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-family: "DM Mono", monospace; font-size: 13px; font-weight: 600; transition: all 0.35s ease; flex-shrink: 0; }
        .step-dot.active { background: #051173; color: #ffffff; border: 2px solid #051173; box-shadow: 0 4px 12px rgba(5,17,115,0.35); }
        .step-dot.done { background: #c7ab5f; color: #ffffff; border: 2px solid #c7ab5f; }
        .step-dot.pending { background: #f5f5f5; color: #051173; border: 1.5px solid #c0c0c0; }
        .step-line { flex: 1; height: 2px; background: #ddd; margin: 0 6px; margin-bottom: 18px; transition: background 0.35s ease; }
        .step-line.done { background: #c7ab5f; }
        .step-label { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.18em; text-transform: uppercase; color: #051173; margin-top: 0.4rem; white-space: nowrap; font-weight: 500; }
        .f-eye { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.32em; text-transform: uppercase; color: #051173; margin-bottom: 0.5rem; }
        .f-title { font-size: 2.4rem; font-weight: 300; font-style: italic; color: #000000; line-height: 0.9; letter-spacing: -0.02em; margin-bottom: 0.5rem; }
        .f-sub { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: #051173; margin-bottom: 1.75rem; line-height: 1.9; }
        .f-email-tag { color: #051173; font-style: normal; }
        .fg { margin-bottom: 1.5rem; position: relative; }
        .fl { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.25em; text-transform: uppercase; color: #000000; margin-bottom: 0.5rem; display: block; }
        .fiw { position: relative; }
        .fi { width: 100%; border: none; border-bottom: 1.5px solid #ddd; background: transparent; padding: 0.7rem 2.5rem 0.7rem 0; font-family: "Cormorant Garamond", Georgia, serif; font-size: 1.05rem; color: #000000; outline: none; transition: border-color 0.25s; letter-spacing: 0.02em; -webkit-appearance: none; border-radius: 0; }
        .fi::placeholder { color: #bbb; font-style: italic; }
        .fi:focus { border-color: #051173; }
        .fi.hv { border-color: var(--gold); }
        .fi.err { border-color: #051173; }
        .fu { position: absolute; bottom: 0; left: 0; width: 0; height: 2px; background: linear-gradient(90deg, #051173, var(--gold)); transition: width 0.35s ease; }
        .fi:focus + .fu { width: 100%; }
        .fi.err + .fu { background: #051173; width: 100%; }
        .ferr { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.1em; color: #051173; margin-top: 0.35rem; display: flex; align-items: center; gap: 0.3rem; }
        .ferr::before { content: "!"; font-weight: 700; }
        .alert { border-radius: 8px; padding: 0.75rem 1rem; margin-bottom: 1rem; font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.08em; text-transform: uppercase; line-height: 1.8; }
        .alert-error { background: rgba(192,57,43,0.08); border: 1px solid rgba(192,57,43,0.22); color: #051173; }
        .alert-success { background: rgba(39,174,96,0.08); border: 1px solid rgba(39,174,96,0.22); color: #051173; }
        .eye-btn { position: absolute; right: 0; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #051173; padding: 0.25rem; transition: color 0.2s; display: flex; align-items: center; -webkit-tap-highlight-color: transparent; min-width: 44px; min-height: 44px; justify-content: center; }
        .otp-row { display: flex; gap: 0.5rem; justify-content: space-between; margin-bottom: 0.5rem; width: 100%; overflow: hidden; }
        .otp-cell { flex: 1; height: 52px; min-width: 0; border: 1.5px solid #051173; border-radius: 8px; background: transparent; font-family: "DM Mono", monospace; font-size: 1.3rem; font-weight: 400; color: #051173; text-align: center; outline: none; transition: border-color 0.2s, box-shadow 0.2s, background 0.2s; -webkit-appearance: none; caret-color: var(--gold); }
        .otp-cell:focus { border-color: #051173; box-shadow: 0 0 0 3px rgba(5,17,115,0.08); background: rgba(5,17,115,0.02); }
        .otp-cell.filled { border-color: var(--gold); background: rgba(199,171,95,0.04); }
        .otp-cell.err-cell { border-color: #051173; }
        .resend-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1.5rem; gap: 1rem; }
        .resend-hint { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: #051173; }
        .resend-btn { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.1em; text-transform: uppercase; color: #051173; background: none; border: none; cursor: pointer; border-bottom: 1px solid transparent; transition: color 0.2s, border-color 0.2s; padding: 0; }
        .resend-btn:hover:not(:disabled) { color: var(--gold); border-color: var(--gold); }
        .resend-btn:disabled { color: rgba(0,0,0,0.25); cursor: not-allowed; }
        .resend-timer { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.1em; color: #051173; }
        .pw-strength { margin-top: 0.5rem; }
        .pw-bars { display: flex; gap: 4px; margin-bottom: 0.3rem; }
        .pw-bar { height: 2px; flex: 1; border-radius: 2px; background: #eee; transition: background 0.3s; }
        .pw-lbl { font-family: "DM Mono", monospace; font-size: 8.5px; letter-spacing: 0.15em; text-transform: uppercase; }
        .sbtn { width: 100%; background: #051173; color: var(--white); border: 2px solid #051173; padding: 1rem 2rem; font-family: "DM Mono", monospace; font-size: 10px; letter-spacing: 0.22em; text-transform: uppercase; cursor: pointer; border-radius: 100px; transition: background 0.3s, border-color 0.3s, transform 0.15s, box-shadow 0.3s; display: flex; align-items: center; justify-content: center; gap: 0.6rem; margin-bottom: 1.25rem; -webkit-tap-highlight-color: transparent; touch-action: manipulation; min-height: 50px; text-decoration: none; }
        .sbtn:hover:not(:disabled) { background: #c7ab5f; border-color: #c7ab5f; transform: translateY(-2px); box-shadow: 0 8px 24px rgba(199,171,95,0.4); }
        .sbtn:active:not(:disabled) { transform: translateY(0); }
        .sbtn:disabled { opacity: 0.65; cursor: not-allowed; }
        .back-link { display: flex; align-items: center; justify-content: center; gap: 0.4rem; font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.15em; text-transform: uppercase; color: #051173; text-decoration: none; transition: color 0.2s; cursor: pointer; background: none; border: none; width: 100%; }
        .spin { width: 13px; height: 13px; border: 2px solid rgba(255,255,255,0.35); border-top-color: white; border-radius: 50%; animation: sp 0.8s linear infinite; }
        @keyframes sp { to { transform: rotate(360deg); } }
        .success-wrap { text-align: center; padding: 1rem 0; }
        .success-icon { width: 64px; height: 64px; border-radius: 50%; background: linear-gradient(135deg, #051173, #0a1fa8); display: flex; align-items: center; justify-content: center; margin: 0 auto 1.5rem; box-shadow: 0 8px 32px rgba(5,17,115,0.25); animation: popIn 0.5s cubic-bezier(0.34,1.56,0.64,1) both; }
        .success-ring { position: relative; display: inline-block; margin-bottom: 1.5rem; }
        .success-ring::before { content: ""; position: absolute; inset: -8px; border-radius: 50%; border: 1px solid rgba(199,171,95,0.4); animation: ringPulse 1.8s ease-out infinite; }
        @keyframes popIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        @keyframes ringPulse { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(1.4); opacity: 0; } }
        .success-title { font-size: 2.2rem; font-weight: 300; font-style: italic; color: #000000; line-height: 0.9; margin-bottom: 0.75rem; }
        .success-sub { font-family: "DM Mono", monospace; font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: #000000; line-height: 2; margin-bottom: 2rem; }
        .slide { animation: slideIn 0.4s cubic-bezier(0.25,0.46,0.45,0.94) both; }
        @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        @media (max-width: 768px) {
          .bg-stats, .bg-compass { display: none; }
          .bg-badge { top: 1.2rem; right: 1.2rem; }
          .c-tl, .c-tr, .c-bl, .c-br { width: 32px; height: 32px; }
          .c-tl { top: 12px; left: 12px; } .c-tr { top: 12px; right: 12px; }
          .c-bl { bottom: 12px; left: 12px; } .c-br { bottom: 12px; right: 12px; }
          .form-wrap { align-items: flex-start; padding-top: 5rem; }
          .fc { padding: 2.25rem 1.75rem 2rem; border-radius: 12px; }
          .f-title { font-size: 2rem; }
          .fi { font-size: 16px; }
          .otp-cell { height: 48px; font-size: 1.2rem; }
        }
        @media (max-width: 380px) {
          .fc { padding: 2rem 1.25rem 1.75rem; }
          .f-title { font-size: 1.8rem; }
          .otp-cell { height: 44px; font-size: 1.1rem; }
          .otp-row { gap: 0.35rem; }
        }
      `}</style>

      <div
        className="fp"
        onMouseMove={(e) => spawnRipple(e.clientX, e.clientY)}
        onClick={(e) => spawnRipple(e.clientX, e.clientY)}
      >
        <video className="bg-video" src="/home.mp4" autoPlay loop muted playsInline />
        <div className="bg-scrim" />
        <div className="bg-tex" />
        <canvas ref={canvasRef} className="bg-canvas" />
        <div className="c-tl" /><div className="c-tr" /><div className="c-bl" /><div className="c-br" />

        <div className="bg-compass">
          <svg width="80" height="80" viewBox="0 0 72 72" fill="none">
            <circle cx="36" cy="36" r="34" stroke="#C7AB5F" strokeWidth="0.7" />
            <circle cx="36" cy="36" r="24" stroke="#C7AB5F" strokeWidth="0.45" />
            <circle cx="36" cy="36" r="4" stroke="#C7AB5F" strokeWidth="0.7" />
            <line x1="36" y1="2" x2="36" y2="70" stroke="#C7AB5F" strokeWidth="0.45" />
            <line x1="2" y1="36" x2="70" y2="36" stroke="#C7AB5F" strokeWidth="0.45" />
            <polygon points="36,4 38,32 36,36 34,32" fill="#C7AB5F" opacity="0.75" />
          </svg>
        </div>

        <div className="bg-stats">
          {[["11+","Years"],["12k","Clients"],["4.9","Rated"]].map(([n,l]) => (
            <div className="bg-stat" key={l}>
              <div className="bg-stat-n">{n}</div>
              <div className="bg-stat-l">{l}</div>
            </div>
          ))}
        </div>

        <div className="bg-badge">Secure Recovery</div>

        <div className="form-wrap">
          <div className={`fc ${mounted ? "on" : ""}`}>

            {stage !== "success" && (
              <div className="steps">
                {[{label:"Email",idx:0},{label:"Verify",idx:1},{label:"Reset",idx:2}].map((s,i) => (
                  <div key={s.label} style={{display:"flex",alignItems:"center",flex:i<2?1:0,color:"#051173"}}>
                    <div className="step-item">
                      <div className={`step-dot ${stageIndex>s.idx?"done":stageIndex===s.idx?"active":"pending"}`}>
                        {stageIndex > s.idx ? <Check size={11} /> : s.idx + 1}
                      </div>
                      <div className="step-label">{s.label}</div>
                    </div>
                    {i < 2 && <div className={`step-line ${stageIndex>s.idx?"done":""}`} style={{alignSelf:"flex-start",marginTop:"18px"}} />}
                  </div>
                ))}
              </div>
            )}

            {apiError && <div className="alert alert-error">{apiError}</div>}
            {successMsg && stage !== "success" && <div className="alert alert-success">{successMsg}</div>}

            {/* ── REQUEST ── */}
            {stage === "request" && (
              <div key="request" className="slide">
                <p className="f-eye">Account Recovery</p>
                <h1 className="f-title">Forgot Password</h1>
                <p className="f-sub">Enter your email and we&apos;ll send<br />a verification code to reset it.</p>
                <div className="fg">
                  <label className="fl" htmlFor="email">Email Address</label>
                  <div className="fiw">
                    <input id="email" type="email" placeholder="your@email.com"
                      className={`fi ${formData.email?"hv":""} ${errors.email?"err":""}`}
                      value={formData.email}
                      onChange={(e) => { setFormData((p)=>({...p,email:e.target.value})); setErrors((p)=>({...p,email:""})); setApiError(""); }}
                      onKeyDown={(e) => e.key==="Enter" && handleRequestSubmit()}
                    />
                    <div className="fu" />
                  </div>
                  {errors.email && <div className="ferr">{errors.email}</div>}
                </div>
                <button className="sbtn" type="button" onClick={handleRequestSubmit} disabled={isLoading}>
                  {isLoading ? <><div className="spin" />Sending Code</> : "Send Verification Code"}
                </button>
                <a className="back-link" href="/login"><ArrowLeft size={12} />Back to Sign In</a>
              </div>
            )}

            {/* ── VERIFY ── */}
            {stage === "verify" && (
              <div key="verify" className="slide">
                <p className="f-eye">Check Your Inbox</p>
                <h1 className="f-title">Enter Code</h1>
                <p className="f-sub">We sent a 6-digit code to<br /><span className="f-email-tag">{formData.email}</span></p>
                <div className="fg">
                  <label className="fl">Verification Code</label>
                  <div className="otp-row" onPaste={handleOtpPaste}>
                    {formData.otp.map((digit, idx) => (
                      <input key={idx} ref={(el) => { otpRefs.current[idx] = el; }}
                        type="text" inputMode="numeric" maxLength={1} value={digit}
                        className={`otp-cell ${digit?"filled":""} ${errors.otp?"err-cell":""}`}
                        onChange={(e) => handleOtpChange(idx, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                        onFocus={(e) => e.target.select()}
                        autoFocus={idx === 0}
                      />
                    ))}
                  </div>
                  {errors.otp && <div className="ferr">{errors.otp}</div>}
                </div>
                <div className="resend-row">
                  <span className="resend-hint">Didn&apos;t receive it?</span>
                  {resendTimer > 0
                    ? <span className="resend-timer">Resend in {resendTimer}s</span>
                    : <button type="button" className="resend-btn" onClick={handleResendOtp} disabled={isLoading}>Resend Code</button>
                  }
                </div>
                <button className="sbtn" type="button" onClick={handleVerifySubmit} disabled={isLoading}>
                  {isLoading ? <><div className="spin" />Verifying</> : "Verify Code"}
                </button>
                <button className="back-link" type="button" onClick={() => { setStage("request"); setErrors({}); setApiError(""); setSuccessMsg(""); }}>
                  <ArrowLeft size={12} />Change Email
                </button>
              </div>
            )}

            {/* ── RESET ── */}
            {stage === "reset" && (
              <div key="reset" className="slide">
                <p className="f-eye">New Password</p>
                <h1 className="f-title">Reset Password</h1>
                <p className="f-sub">Choose a strong new password<br />for your account.</p>
                <div className="fg">
                  <label className="fl" htmlFor="newpassword">New Password</label>
                  <div className="fiw">
                    <input id="newpassword" type={showNew?"text":"password"} placeholder="Min. 8 characters"
                      className={`fi ${formData.newpassword?"hv":""} ${errors.newpassword?"err":""}`}
                      value={formData.newpassword}
                      onChange={(e) => { setFormData((p)=>({...p,newpassword:e.target.value})); setErrors((p)=>({...p,newpassword:""})); setApiError(""); }}
                    />
                    <div className="fu" />
                    <button type="button" className="eye-btn" onClick={() => setShowNew(!showNew)}>
                      {showNew
                        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      }
                    </button>
                  </div>
                  {formData.newpassword && (
                    <div className="pw-strength">
                      <div className="pw-bars">
                        {[1,2,3,4].map((i) => <div key={i} className="pw-bar" style={{background:i<=pwStrength?strengthColor:"#eee"}} />)}
                      </div>
                      <span className="pw-lbl" style={{color:strengthColor}}>{strengthLabel}</span>
                    </div>
                  )}
                  {errors.newpassword && <div className="ferr">{errors.newpassword}</div>}
                </div>
                <div className="fg">
                  <label className="fl" htmlFor="confirmpassword">Confirm Password</label>
                  <div className="fiw">
                    <input id="confirmpassword" type={showConfirm?"text":"password"} placeholder="Repeat password"
                      className={`fi ${formData.confirmpassword?"hv":""} ${errors.confirmpassword?"err":""}`}
                      value={formData.confirmpassword}
                      onChange={(e) => { setFormData((p)=>({...p,confirmpassword:e.target.value})); setErrors((p)=>({...p,confirmpassword:""})); setApiError(""); }}
                    />
                    <div className="fu" />
                    <button type="button" className="eye-btn" onClick={() => setShowConfirm(!showConfirm)}>
                      {showConfirm
                        ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                        : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                      }
                    </button>
                  </div>
                  {errors.confirmpassword && <div className="ferr">{errors.confirmpassword}</div>}
                </div>
                <button className="sbtn" type="button" onClick={handleResetSubmit} disabled={isLoading}>
                  {isLoading ? <><div className="spin" />Updating Password</> : "Update Password"}
                </button>
                <button className="back-link" type="button" onClick={() => { setStage("verify"); setErrors({}); setApiError(""); setSuccessMsg(""); }}>
                  <ArrowLeft size={12} />Back to Verify
                </button>
              </div>
            )}

            {/* ── SUCCESS ── */}
            {stage === "success" && (
              <div key="success" className="slide success-wrap">
                <div className="success-ring">
                  <div className="success-icon"><Check size={28} color="white" strokeWidth={2.5} /></div>
                </div>
                <h1 className="success-title">Password Updated</h1>
                <p className="success-sub">Your password has been reset<br />successfully. You can now<br />sign in with your new password.</p>
                <a className="sbtn" href="/login" style={{textDecoration:"none"}}>Sign In to Studio</a>
              </div>
            )}

          </div>
        </div>
      </div>
    </>
  );
}